import { toastr } from 'react-redux-toastr';
import _ from 'lodash';
import {
    pendingTask,
    begin,
    end,
} from 'react-redux-spinner';
import BriefBidProfileModelBuilder from '../../services/BriefBidProfileModelBuilder';
import { UPDATE_BID_PROFILE_MANAGEMENT } from './bidProfileManagement.actions';
import { RESET_EMPLOYEE_PREFERENCES_REPORT } from '../../../store/employeePreferencesReport';
import { RESET_UNAWARDED_SHIFTLINES_REPORT } from '../../../store/unawardedShiftlinesReport';
import { RESET_EMPLOYEE_BIDDING_RESULTS_REPORT } from '../../../store/employeeBiddingResultsReport';
import { UPDATE_MANAGER_APP_DATA } from '../appData';
import { UPDATE_BID_PROFILE, UPDATE_SCHEDULE_SELECTION, UPDATE_ORIGINAL_SCHEDULE_SELECTION } from '../bidProfile';
import { LOADING_END, LOADING_START } from '../../../store/loading';
import { UPDATE_WORKPLAN_PUBLISH_PROGRESS } from '../workplanPublishProgress';
import * as api from '../../services/api-endpoints';
import {
    simplifyBidProfileModels,
    simplifyAwardingResult,
    isScheduleSelectionEditable,
    simplifyScheduleTemplates,
} from '../../services/BidProfileModelMapper';
import i18n from '../../../services/i18n';
import { BidProfileEditTab, PublishStatusType, WorkplanPublishStepCode } from '../../../Enums';

/**
 * @param {string} profileId
 * @param {{
 *  hyperfinds: ManagerAppData.IHyperfind[],
 *  managerBidSetups: ManagerAppData.IManagerBidSetup[],
 * }} storeData
 * @param {BidProfileEditTab} tabSelected
 */
// eslint-disable-next-line max-len
export const refreshBidDashboard = (profileId, storeData, tabSelected) => /** @param {import('redux').Dispatch} dispatch */async (dispatch) => { // VERY SIMILAR TO loadBidProfileEdit
    const {
        hyperfinds, managerBidSetups,
    } = storeData;
    try {
        dispatch({ type: LOADING_START, [pendingTask]: begin });
        const promises = await Promise.all([
            api.getBidProfileManagementInfos(profileId),
            api.getBidProfile(profileId),
            api.GetBidProfileScheduleItems(profileId),
        ]);

        if (promises.every((x) => x.status === 200)) {
            const [
                managementInfoResponse,
                bidProfileResponse,
                scheduleTemplatesResponse,
            ] = promises;
            const [bidProfile] = simplifyBidProfileModels(
                [bidProfileResponse.data],
                {
                    hyperfinds,
                    managerBidSetups,
                },

                tabSelected,
            );
            dispatch({ type: UPDATE_BID_PROFILE, payload: { ...bidProfile } });
            const canEditScheduleSelection = isScheduleSelectionEditable(bidProfile.status);
            const scheduleTemplates = simplifyScheduleTemplates(scheduleTemplatesResponse.data, canEditScheduleSelection);
            dispatch({ type: UPDATE_SCHEDULE_SELECTION, payload: scheduleTemplates });
            dispatch({ type: UPDATE_ORIGINAL_SCHEDULE_SELECTION, payload: _.cloneDeep(scheduleTemplates) });
            dispatch({
                type: UPDATE_BID_PROFILE_MANAGEMENT,
                payload: {
                    profileId: _.cloneDeep(profileId),
                    employeesBidSelection: managementInfoResponse.data.employeesBidSelection,
                    awardingResults: simplifyAwardingResult(
                        managementInfoResponse.data.awardingResults,
                        scheduleTemplates.items,
                    ),
                    isBidOnBehalfAllowed: managementInfoResponse.data.isBidOnBehalfAllowed,
                    isEditBidOnBehalfAllowed: managementInfoResponse.data.isEditBidOnBehalfAllowed,
                    isRecalculateEmployeeRanksAllowed: managementInfoResponse.data.isRecalculateEmployeeRanksAllowed,
                    isAwardingEmployees: managementInfoResponse.data.isAwardingEmployees,
                },
            });
            dispatch({
                type: UPDATE_MANAGER_APP_DATA,
                payload: {
                    pageName: i18n.t('shiftBidding:manager.common.editBid'),
                },
            });
            dispatch({ type: RESET_EMPLOYEE_PREFERENCES_REPORT });
            dispatch({ type: RESET_UNAWARDED_SHIFTLINES_REPORT });
            dispatch({ type: RESET_EMPLOYEE_BIDDING_RESULTS_REPORT });
        } else {
            toastr.error(i18n.t('shiftBidding:toastr.fetchingBidSubmissions'), i18n.t('shiftBidding:toastr.failedToFetchBidSubmissions'));
        }
    } catch (error) {
        toastr.error(i18n.t('shiftBidding:toastr.fetchingBidSubmissions'), error.response.data.Message);
    } finally {
        dispatch({ type: LOADING_END, [pendingTask]: end });
    }
};

/**
 * @param {string} profileId
 * @param {BidProfile.IScheduleTemplateItem[]} scheduleTemplatesItems
 */
export const recalculateEmployeeRanks = (profileId, scheduleTemplatesItems) =>
/** @param {import('redux').Dispatch} dispatch */async (dispatch) => {
        try {
            dispatch({ type: LOADING_START, [pendingTask]: begin });
            const result = await api.recalculateEmployeeRanks(profileId);
            if (result.status === 200) {
                const awardingResults = simplifyAwardingResult(result.data.awardingResults, scheduleTemplatesItems);

                dispatch({
                    type: UPDATE_BID_PROFILE_MANAGEMENT,
                    payload: {
                        awardingResults,
                    },
                });
                dispatch({ type: RESET_EMPLOYEE_PREFERENCES_REPORT });
                dispatch({ type: RESET_UNAWARDED_SHIFTLINES_REPORT });
                dispatch({ type: RESET_EMPLOYEE_BIDDING_RESULTS_REPORT });
                toastr.success(i18n.t('shiftBidding:toastr.recalculatingEmployeeRanks'), i18n.t('shiftBidding:toastr.employeeRanksRecalculatedSuccessfully'));
            } else {
                toastr.error(i18n.t('shiftBidding:toastr.recalculatingEmployeeRanks'), i18n.t('shiftBidding:toastr.failedToRecalculateEmployeeRanks'));
            }
        } catch (error) {
            toastr.error(i18n.t('shiftBidding:toastr.recalculatingEmployeeRanks'), error.response.data.Message);
        } finally {
            dispatch({ type: LOADING_END, [pendingTask]: end });
        }
    };

/**
 * @param {string} profileId
 * @param {number[]} employeeIds
 * @param {BidProfile.IScheduleTemplateItem[]} scheduleTemplatesItems
 */
export const approveBids = (profileId, employeeIds, scheduleTemplatesItems) =>
/** @param {import('redux').Dispatch} dispatch */async (dispatch) => {
        try {
            dispatch({ type: LOADING_START, [pendingTask]: begin });
            const result = await api.approveBids(profileId, employeeIds);
            if (result.status === 200) {
                dispatch({
                    type: UPDATE_BID_PROFILE_MANAGEMENT,
                    payload: {
                        employeesBidSelection: result.data.employeesBidSelection,
                        awardingResults: simplifyAwardingResult(result.data.awardingResults, scheduleTemplatesItems),
                        checkedKeys: [],
                    },
                });
                toastr.success(i18n.t('shiftBidding:toastr.approvingBidSubmissions'), i18n.t('shiftBidding:toastr.submissionsApprovedSuccessfully'));
            } else {
                toastr.error(i18n.t('shiftBidding:toastr.approvingBidSubmissions'), i18n.t('shiftBidding:toastr.failedToApproveBidSubmissions'));
            }
        } catch (error) {
            toastr.error(i18n.t('shiftBidding:toastr.approvingBidSubmissions'), error.response.data.Message);
        } finally {
            dispatch({ type: LOADING_END, [pendingTask]: end });
        }
    };

/**
 * @param {string} profileId
 * @param {number[]} employeeIds
 * @param {BidProfile.IScheduleTemplateItem[]} scheduleTemplatesItems
 */
export const refuseBids = (profileId, employeeIds, scheduleTemplatesItems) =>
/** @param {import('redux').Dispatch} dispatch */async (dispatch) => {
        try {
            dispatch({ type: LOADING_START, [pendingTask]: begin });
            const result = await api.refuseBids(profileId, employeeIds);
            if (result.status === 200) {
                return dispatch({
                    type: UPDATE_BID_PROFILE_MANAGEMENT,
                    payload: {
                        employeesBidSelection: result.data.employeesBidSelection,
                        awardingResults: simplifyAwardingResult(result.data.awardingResults, scheduleTemplatesItems),
                        checkedKeys: [],
                    },
                });
            }

            toastr.error(i18n.t('shiftBidding:toastr.errorRefusingBidSubmissions'), i18n.t('shiftBidding:toastr.failedToRefuseBidSubmissions'));
            return Promise.reject();
        } catch (error) {
            toastr.error(i18n.t('shiftBidding:toastr.refusingBidSubmissions'), error.response.data.Message);
            return Promise.reject(error);
        } finally {
            dispatch({ type: LOADING_END, [pendingTask]: end });
        }
    };

/**
 * @param {string} profileId
 * @param {number[]} expandedRowKeys
 * @param {BidProfile.IScheduleTemplateItem[]} scheduleTemplatesItems
 */
export const calculateAwarding = (profileId, expandedRowKeys, scheduleTemplatesItems) =>
/** @param {import('redux').Dispatch} dispatch */async (dispatch) => {
        try {
            dispatch({ type: LOADING_START, [pendingTask]: begin });
            const result = await api.recalculateAwarding(profileId);
            if (result.status === 200) {
                const awardingResults = simplifyAwardingResult(result.data.awardingResults, scheduleTemplatesItems);

                // Unexpand rows with no awardings
                const updatedExpandedRowKeys = [...expandedRowKeys];
                updatedExpandedRowKeys.forEach((x, index) => {
                    const expandedKeyAward = awardingResults.find((a) => a.employeeId === x);
                    if (!expandedKeyAward?.hasAwardedShiftline) {
                        updatedExpandedRowKeys.splice(index, 1);
                    }
                });
                dispatch({
                    type: UPDATE_BID_PROFILE_MANAGEMENT,
                    payload: {
                        employeesBidSelection: result.data.employeesBidSelection,
                        awardingResults,
                        expandedRowKeys: updatedExpandedRowKeys,
                        isAwardingEmployees: result.data.isAwardingEmployees,
                    },
                });
                dispatch({ type: RESET_EMPLOYEE_PREFERENCES_REPORT });
                dispatch({ type: RESET_UNAWARDED_SHIFTLINES_REPORT });
                dispatch({ type: RESET_EMPLOYEE_BIDDING_RESULTS_REPORT });
                toastr.success(i18n.t('shiftBidding:toastr.recalculationStarted'), '');
            } else {
                toastr.error(i18n.t('shiftBidding:toastr.recalculationStarted'), i18n.t('shiftBidding:toastr.failedToRecalculateAwarding'));
            }
        } catch (error) {
            toastr.error(i18n.t('shiftBidding:toastr.recalculationStarted'), error.response.data.Message);
        } finally {
            dispatch({ type: LOADING_END, [pendingTask]: end });
        }
    };

/**
 * @param {string} profileId
 * @param {number[]} employeeIds
 * @param {number[]} expandedRowKeys
 * @param {BidProfile.IScheduleTemplateItem[]} scheduleTemplatesItems
 */
export const refuseAndCalculate = (profileId, employeeIds, expandedRowKeys, scheduleTemplatesItems) => /** @param {import('redux').Dispatch} dispatch */async (dispatch) => {
    dispatch({ type: LOADING_START, [pendingTask]: begin });
    dispatch(refuseBids(profileId, employeeIds, scheduleTemplatesItems)).then((promise) => {
        dispatch(calculateAwarding(profileId, expandedRowKeys, scheduleTemplatesItems));
    });
    dispatch({ type: LOADING_END, [pendingTask]: end });
};

/**
 * @param {string} profileId
 * @param {{
 *  bidProfiles: BidProfile.IBriefBaseProfile[],
 *  hyperfinds: ManagerAppData.IHyperfind[],
 *  managerBidSetups: ManagerAppData.IManagerBidSetup[],
 * }} storeData
 */
export const publishBid = (profileId, storeData) => /** @param {import('redux').Dispatch} dispatch */async (dispatch) => {
    const {
        bidProfiles,
    } = storeData;
    
    dispatch({
        type: UPDATE_WORKPLAN_PUBLISH_PROGRESS,
        payload: {
            progressValue: 0,
            progressStepCode: WorkplanPublishStepCode.Loading,
            isComplete: false,
            stepInfo: [],
            status: PublishStatusType.Publishing
        },
    });
    
    dispatch({
        type: UPDATE_BID_PROFILE_MANAGEMENT,
        payload: { rolloutErrors: [] }
    });
    
    try {
        dispatch({ type: LOADING_START, [pendingTask]: begin });
        const result = await api.completeBid(profileId);
        if (result.status === 200) {
            const latestStatus = result.data.status;
            dispatch({
                type: UPDATE_BID_PROFILE,
                payload: { status: latestStatus },
            });

            const [bidProfile] = simplifyBidProfileModels([result.data], storeData, BidProfileEditTab.Dashboard);
            dispatch({ type: UPDATE_BID_PROFILE, payload: { ...bidProfile } });
            const publishedProfile = BriefBidProfileModelBuilder.fromBidProfilePresentationModel(result.data, storeData);
            const profileToUpdateIndex = bidProfiles.findIndex((x) => x.id === profileId);
            bidProfiles.splice(profileToUpdateIndex, 1, _.cloneDeep(publishedProfile));

            dispatch({ type: UPDATE_MANAGER_APP_DATA, payload: { bidProfiles } });
            toastr.success(i18n.t('shiftBidding:toastr.publishingBidApprovals'), i18n.t('shiftBidding:toastr.bidApprovalsPublishStarted'));
            return;
        }

        toastr.error(i18n.t('shiftBidding:toastr.errorPublishingBidApprovals'), i18n.t('shiftBidding:toastr.publishActionInterrupted'));
    } catch (error) {
        toastr.error(i18n.t('shiftBidding:toastr.publishingBidApprovals'), error.response.data.Message ?? i18n.t('shiftBidding:toastr.publishActionInterrupted'));
    } finally {
        dispatch({ type: LOADING_END, [pendingTask]: end });
    }
};

/**
 * @param {string} profileId
 * @param {{
 *  bidProfiles: BidProfile.IBriefBaseProfile[],
 *  hyperfinds: ManagerAppData.IHyperfind[],
 *  managerBidSetups: ManagerAppData.IManagerBidSetup[],
 * }} storeData
 */
export const cancelBid = (profileId, storeData) => /** @param {import('redux').Dispatch} dispatch */async (dispatch) => {
    const {
        bidProfiles,
    } = storeData;
    try {
        dispatch({ type: LOADING_START, [pendingTask]: begin });
        const result = await api.cancelBid(profileId);
        if (result.status === 200) {
            toastr.success(i18n.t('shiftBidding:toastr.cancelingBid'), i18n.t('shiftBidding:toastr.bidCanceledSuccessfully'));

            const latestStatus = result.data.status;
            dispatch({
                type: UPDATE_BID_PROFILE,
                payload: { status: latestStatus },
            });

            const cancelledProfile = BriefBidProfileModelBuilder.fromBriefBidProfilePresentationModel(result.data, storeData);
            const profileToUpdateIndex = bidProfiles.findIndex((x) => x.id === profileId);
            bidProfiles.splice(profileToUpdateIndex, 1, _.cloneDeep(cancelledProfile));

            dispatch({ type: UPDATE_MANAGER_APP_DATA, payload: { bidProfiles } });
            toastr.success(i18n.t('shiftBidding:toastr.cancelingBid'), i18n.t('shiftBidding:toastr.bidCanceledSuccessfully'));
            return;
        }
        toastr.error(i18n.t('shiftBidding:toastr.errorCancelingBid'), i18n.t('shiftBidding:toastr.failedToCancelBid'));
    } catch (error) {
        toastr.error(i18n.t('shiftBidding:toastr.cancelingBid'), error.response.data.Message);
    } finally {
        dispatch({ type: LOADING_END, [pendingTask]: end });
    }
};

/**
 * @param {string} profileId
 * @param {{
 *  bidProfiles: BidProfile.IBriefBaseProfile[],
*  hyperfinds: ManagerAppData.IHyperfind[],
* }} storeData
*/
export const updateCompletedBidProfile = (profileId, storeData) => /** @param {import('redux').Dispatch} dispatch */async (dispatch) => {
    const {
        bidProfiles,
    } = storeData;
    
    // Update this first so that we stop pinging /status
    dispatch({
        type: UPDATE_BID_PROFILE,
        payload: { isPublishingToWorkplan: false },
    });
    try {
        dispatch({ type: LOADING_START, [pendingTask]: begin });
        const result = await api.getBidProfile(profileId);
        if (result.status === 200) {
            const { status, isPublishingToWorkplan } = result.data;

            dispatch({
                type: UPDATE_BID_PROFILE,
                payload: { status, isPublishingToWorkplan },
            });

            const publishedProfile = BriefBidProfileModelBuilder.fromBidProfilePresentationModel(result.data, storeData);
            const profileToUpdateIndex = bidProfiles.findIndex((x) => x.id === profileId);
            bidProfiles.splice(profileToUpdateIndex, 1, _.cloneDeep(publishedProfile));

            dispatch({ type: UPDATE_MANAGER_APP_DATA, payload: { bidProfiles } });

            try {
                const bidProfileManagementResult = await api.getBidProfileManagementInfos(profileId);
                if (bidProfileManagementResult.status === 200) {
                    const { rolloutErrors } = bidProfileManagementResult.data;
                    dispatch({
                        type: UPDATE_BID_PROFILE_MANAGEMENT,
                        payload: { rolloutErrors }
                    });
                    if (rolloutErrors && rolloutErrors.length > 0) {
                        toastr.error(i18n.t('shiftBidding:toastr.publishingBid'), i18n.t('shiftBidding:toastr.rolloutFailed'));
                    } else {
                        toastr.success(i18n.t('shiftBidding:toastr.refreshingBid'), i18n.t('shiftBidding:toastr.bidRefreshedSuccessfully'));
                    }
                } else {
                    toastr.error(i18n.t('shiftBidding:toastr.errorRefreshingBid'), i18n.t('shiftBidding:toastr.failedToRefreshBid'));
                }
            } catch (progressError) {
                toastr.error(i18n.t('shiftBidding:toastr.errorRefreshingBid'), i18n.t('shiftBidding:toastr.failedToRefreshBid'));
            }

            return;
        }
        toastr.error(i18n.t('shiftBidding:toastr.errorRefreshingBid'), i18n.t('shiftBidding:toastr.failedToRefreshBid'));
    } catch (error) {
        toastr.error(i18n.t('shiftBidding:toastr.refreshingBid'), error.response.data.Message);
    } finally {
        dispatch({ type: LOADING_END, [pendingTask]: end });
    }
};

/**
 * @param {string} profileId
* @param {number[]} expandedRowKeys
* @param {BidProfile.IScheduleTemplateItem[]} scheduleTemplatesItems
*/
export const updateAwardingCompletedBidProfile = (profileId, expandedRowKeys, scheduleTemplatesItems) => /** @param {import('redux').Dispatch} dispatch */async (dispatch) => {
    // Update this first so that we stop pinging /status
    dispatch({
        type: UPDATE_BID_PROFILE_MANAGEMENT,
        payload: { isAwardingEmployees: false },
    });
    try {
        dispatch({ type: LOADING_START, [pendingTask]: begin });
        const result = await api.getBidProfileManagementInfos(profileId);
        if (result.status === 200) {
            const awardingResults = simplifyAwardingResult(result.data.awardingResults, scheduleTemplatesItems);

            // Unexpand rows with no awardings
            const updatedExpandedRowKeys = [...expandedRowKeys];
            updatedExpandedRowKeys.forEach((x, index) => {
                const expandedKeyAward = awardingResults.find((a) => a.employeeId === x);
                if (!expandedKeyAward?.hasAwardedShiftline) {
                    updatedExpandedRowKeys.splice(index, 1);
                }
            });
            dispatch({
                type: UPDATE_BID_PROFILE_MANAGEMENT,
                payload: {
                    employeesBidSelection: result.data.employeesBidSelection,
                    awardingResults,
                    expandedRowKeys: updatedExpandedRowKeys,
                    isAwardingEmployees: result.data.isAwardingEmployees,
                },
            });
            dispatch({ type: RESET_EMPLOYEE_PREFERENCES_REPORT });
            dispatch({ type: RESET_UNAWARDED_SHIFTLINES_REPORT });
            dispatch({ type: RESET_EMPLOYEE_BIDDING_RESULTS_REPORT });
            toastr.success(i18n.t('shiftBidding:toastr.refreshingAwardings'), i18n.t('shiftBidding:toastr.awardingRecalculatedSuccessfully'));

            return;
        }
        toastr.error(i18n.t('shiftBidding:toastr.errorRefreshingAwarding'), i18n.t('shiftBidding:toastr.failedToRefreshAwarding'));
    } catch (error) {
        toastr.error(i18n.t('shiftBidding:toastr.refreshingAwarding'), error.response.data.Message);
    } finally {
        dispatch({ type: LOADING_END, [pendingTask]: end });
    }
};
